/**
 * What the save machine looks like on screen: the status at the end of the
 * map's toolbar, and the banner that stops the user when somebody else saved
 * the same map first.
 *
 * The status sits in a polite live region, so a screen reader hears "saved"
 * without the focus leaving the canvas. "idle" (loaded, untouched) says
 * nothing: a line announcing a state nobody caused is noise.
 *
 * The retry button wears the map's own toolbar class (`wa-fm__btn`, part of the
 * widget's shared CSS contract) because it sits in that toolbar, beside the
 * map's buttons, not among the CRM's.
 */
import { Check, CircleDot, Loader2, RotateCcw, TriangleAlert, type LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { Btn } from "../../../ui";
import type { SaveState, SaveStatusKind } from "./saveState";

/** A `Record`, so a status added to the machine without a face here is a type error. */
const LOOK: Record<SaveStatusKind, { key: string; Icon: LucideIcon; tone: string; spin?: boolean } | null> = {
  idle: null,
  dirty: { key: "flowMap.status.dirty", Icon: CircleDot, tone: "text-muted-foreground" },
  saving: { key: "flowMap.status.saving", Icon: Loader2, tone: "text-muted-foreground", spin: true },
  saved: { key: "flowMap.status.saved", Icon: Check, tone: "text-muted-foreground" },
  conflict: { key: "flowMap.status.conflict", Icon: TriangleAlert, tone: "text-amber-600" },
  error: { key: "flowMap.status.error", Icon: TriangleAlert, tone: "text-destructive" },
};

export function SaveStatus({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  const t = useT();
  const look = LOOK[state.status];
  return (
    <span className="inline-flex items-center gap-2">
      <span
        role="status"
        aria-live="polite"
        className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-medium", look?.tone)}
      >
        {look ? (
          <>
            <look.Icon className={cn("size-3.5 shrink-0", look.spin && "animate-spin")} aria-hidden="true" />
            {t(look.key)}
          </>
        ) : null}
      </span>
      {state.status === "error" ? (
        <button type="button" className="wa-fm__btn" onClick={onRetry}>
          <RotateCcw className="size-3.5" aria-hidden="true" />
          {t("flowMap.retry")}
        </button>
      ) : null}
    </span>
  );
}

/**
 * Somebody else saved this map while this user was editing it.
 *
 * Both ways out are spelled out in the sentence, because both lose something:
 * loading the latest drops this user's recent edits, keeping theirs replaces
 * the other save. Neither is chosen for them.
 */
export function ConflictBanner({
  who,
  onLoadLatest,
  onKeepMine,
}: {
  who: string | null;
  onLoadLatest: () => void;
  onKeepMine: () => void;
}) {
  const t = useT();
  return (
    <div
      role="alert"
      className="mx-4 mt-4 flex flex-wrap items-center gap-3 rounded-2xl border-l-[3px] border-amber-500 bg-amber-500/10 px-4 py-3 sm:mx-[26px]"
    >
      <TriangleAlert className="size-4 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-[200px] flex-1">
        <strong className="block text-[13.5px]">{t("flowMap.conflict.title")}</strong>
        <p className="crm-mut mt-0.5 text-[12.5px] leading-snug">
          {who ? t("flowMap.conflict.bodyBy", { who }) : t("flowMap.conflict.body")}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn small onClick={onLoadLatest}>
          {t("flowMap.conflict.loadLatest")}
        </Btn>
        <Btn small tone="dark" onClick={onKeepMine}>
          {t("flowMap.conflict.keepMine")}
        </Btn>
      </div>
    </div>
  );
}
